import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';

/** The extension lengths the legacy trial-extension flow offers. */
export const TRIAL_EXTENSION_DAYS = [7, 14, 30] as const;

export type TrialExtensionDays = (typeof TRIAL_EXTENSION_DAYS)[number];

export interface IExtendTrialRequest {
	/** The trial account being extended. */
	accountId: string;
	/** How many days to extend by. */
	durationInDays: TrialExtensionDays;
}

/**
 * Extends a trial via `PUT admin/account/{id}/trialExtension` — the react-query replacement for the
 * MobX `AccountOperationsViewModel.extendTrial`. The duration is the request body (a bare number, as
 * legacy sent it) and the updated account is returned. Mutations have no MutationCache, so the error
 * toast is wired here explicitly.
 */
export function useExtendTrialMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ accountId, durationInDays }: IExtendTrialRequest) =>
			userSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${accountId}/trialExtension`,
				'PUT',
				durationInDays
			),
		onError: () => toast.error('Failed to extend the trial. Please try again.'),
	});
}
